/**
 * Activity Service
 *
 * Handles API calls for activity/audit log functionality
 */

import { get } from "./api";
import { ActivityLog } from "@/types";
import { API_ROUTES } from "@/utils/constants";

// Backend audit log response type (from aixa-audit-logs container)
interface BackendAuditLog {
  id: string;
  operation: string;
  resourceType: string;
  resourceIds: string[];
  userId: string; // Email
  userOid: string; // Object ID
  userAmr?: string;
  invocationId: string;
  correlationId: string;
  ipAddress: string;
  timestamp: string;
  metadata: Record<string, unknown>;
  ttl: number;
}

// Map operation to action for frontend compatibility
const operationToAction: Record<string, ActivityLog["action"]> = {
  UPLOAD: "upload",
  DOWNLOAD: "download",
  VIEW: "view",
  SHARE: "share",
  DELETE: "delete",
  AUTO_DELETE: "delete", // Auto-delete maps to delete action
  GRANT_ACCESS: "share",
  REVOKE_ACCESS: "unshare",
  CREATE_FOLDER: "upload",
  UPDATE_FOLDER: "rename",
  DELETE_FOLDER: "delete",
};

/**
 * Map backend audit log to frontend ActivityLog format
 */
function mapActivity(audit: BackendAuditLog): ActivityLog {
  const isSystem = audit.userOid === "system";

  return {
    id: audit.id,
    action: operationToAction[audit.operation] || "view",
    resourceType: audit.resourceType.toLowerCase() as ActivityLog["resourceType"],
    resourceId: audit.resourceIds[0] || "",
    resourceName:
      (audit.metadata?.resourceName as string) ||
      (audit.metadata?.fileName as string) ||
      (audit.metadata?.folderName as string) ||
      "",
    performedBy: isSystem
      ? {
          id: "system",
          name: "System",
          email: "",
          role: "staff",
        }
      : {
          id: audit.userOid,
          name: audit.userId.split("@")[0], // Use email prefix as name
          email: audit.userId,
          role: "staff",
        },
    performedAt: audit.timestamp,
    ipAddress: audit.ipAddress || "",
    userAgent: "",
    details: {
      ...audit.metadata,
      operation: audit.operation,
    },
    _partitionKey: audit.resourceIds[0] || "",
  };
}

/**
 * Get activities for a specific resource (file or folder)
 */
export async function getResourceActivity(
  resourceId: string,
  limit: number = 20
): Promise<ActivityLog[]> {
  const activities = await get<BackendAuditLog[]>(
    `${API_ROUTES.activity}?resourceId=${resourceId}&limit=${limit}`
  );
  return activities.map(mapActivity);
}

/**
 * Get recent activities for the current user
 */
export async function getUserActivity(
  options: { limit?: number; resourceType?: "file" | "folder" } = {}
): Promise<ActivityLog[]> {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.resourceType) params.append("resourceType", options.resourceType);

  const queryString = params.toString();
  const url = queryString
    ? `${API_ROUTES.activity}?${queryString}`
    : API_ROUTES.activity;

  const activities = await get<BackendAuditLog[]>(url);
  return activities.map(mapActivity);
}
